import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState, type ReactElement } from "react";
import { Field, Modal, useToast } from "@cx/ui";
import type { FiscalDocumentView, PaymentMethodView, TaxOperationListResult } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";

type Props = { open: boolean; orderId: string; onClose(): void; onEmitted(d: FiscalDocumentView): void };

/**
 * "Emitir NF-e" do pedido (T2.3): natureza (padrão = a da empresa) e forma de pagamento das parcelas (padrão =
 * "sem forma informada"). A nota sai com os itens, cliente, frete e parcelas do pedido e vai direto para a SEFAZ.
 */
export function EmitInvoiceDialog({ open, orderId, onClose, onEmitted }: Props): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const toast = useToast();
  const [operationId, setOperationId] = useState("");
  const [methodId, setMethodId] = useState("");
  // Uma chave por abertura do diálogo: o duplo clique não emite duas vezes.
  const [key, setKey] = useState(() => crypto.randomUUID());
  useEffect(() => {
    if (open) setKey(crypto.randomUUID());
  }, [open]);
  const ops = useQuery({
    queryKey: ["tax-operations", companyId, "picker"],
    enabled: open && can(perms, "tax_operations", "read"),
    queryFn: () => api<TaxOperationListResult>("/tax-operations?status=active&limit=100"),
  });
  const methods = useQuery({
    queryKey: ["payment-methods", companyId],
    enabled: open && can(perms, "payment_methods", "read"),
    queryFn: () => api<{ data: PaymentMethodView[] }>("/payment-methods"),
  });
  const emit = useMutation({
    mutationFn: () =>
      api<FiscalDocumentView>(`/sales-orders/${orderId}/invoice`, {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ taxOperationId: operationId || null, paymentMethodId: methodId || null }),
      }),
    onSuccess: (d) => {
      toast("info", `NF-e nº ${d.number ?? ""} enviada à SEFAZ`);
      onEmitted(d);
    },
    onError: (e) => {
      toast("danger", errorMessage(e));
      // Dados incompletos (nfe_incomplete) não queimam número: nova chave para a próxima tentativa.
      if (e instanceof ApiFailure && e.status !== 409) setKey(crypto.randomUUID());
    },
  });
  const outs = (ops.data?.data ?? []).filter((o) => o.direction === "out");
  return (
    <Modal
      open={open}
      title="Emitir NF-e"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={emit.isPending}
            onClick={() => emit.mutate()}
          >
            {emit.isPending ? "Emitindo…" : "Emitir NF-e"}
          </button>
        </>
      }
    >
      <p className="small text-muted">
        A nota leva os itens, o cliente, o frete e as parcelas do pedido. O número é reservado ao emitir e o
        resultado da SEFAZ aparece na tela da nota.
      </p>
      <div className="row">
        <Field label="Natureza da operação" className="col-12">
          <select
            className="form-select"
            value={operationId}
            data-autofocus
            onChange={(e) => setOperationId(e.target.value)}
          >
            <option value="">Padrão da empresa</option>
            {outs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Forma de pagamento" className="col-12" hint="Vale para todas as parcelas do pedido.">
          <select className="form-select" value={methodId} onChange={(e) => setMethodId(e.target.value)}>
            <option value="">Sem forma informada</option>
            {(methods.data?.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </Modal>
  );
}
